import React from "react";
import Seo from "../components/Seo";

export default function TermsOfService() {
  const termsText = `MANUFACTURING AUTOMATION GROUP LLC
Terms of Service
Effective Date: January 2026
PROPRIETARY & CONFIDENTIAL. This document and its contents are the exclusive property of Manufacturing Automation Group LLC. Reproduction for commercial purposes is prohibited without written consent from MAG.

IMPORTANT: By submitting a purchase order, accepting a quotation, or otherwise engaging Manufacturing Automation Group LLC for goods or services, you agree to be bound by these Terms of Service in their entirety. Please read them carefully before proceeding.

1. About Us
Manufacturing Automation Group LLC ("MAG," "we," "us," or "our") is a Michigan-based limited liability company specializing in AGV and AMR integration services for manufacturing automation. Our principal place of business is located at 35572 Veronica St., Livonia, MI 48150.
These Terms of Service ("Terms") govern all sales of goods and services by MAG to any customer, purchaser, or buyer ("Customer," "you," or "your"). These Terms apply to all quotations, purchase orders, contracts, and project engagements.

2. Acceptance of Terms
Your agreement to these Terms is formed when any of the following occurs:
- You submit a purchase order to MAG;
- You formally accept a quotation or proposal issued by MAG; or
- You authorize MAG to begin work, whether verbally, in writing, or by conduct.
Any terms proposed by you that are additional to, or inconsistent with, these Terms are expressly rejected unless agreed to in a separate written instrument signed by an authorized officer of MAG. These Terms supersede any terms printed on Customer purchase orders or other Customer-issued documents.

3. Quotations and Orders
All quotations issued by MAG are valid for thirty (30) days from the date of issue unless otherwise stated. MAG reserves the right to withdraw or modify a quotation at any time prior to formal acceptance. Accepted orders may not be modified or canceled without MAG's prior written consent and may be subject to fees as described in Section 9 (Order Cancellation).

4. Payment Terms
4.1 General Payment Terms
- All invoices are due Net 30 days from invoice date unless otherwise agreed in writing.
- Payments shall be made via wire transfer or ACH to MAG-designated accounts.
- Customer is responsible for all transfer-related fees.
- Late payments are subject to 1.5% interest per month (or maximum allowed by law).
- MAG reserves the right to suspend work, withhold deliverables, or delay shipment in the event of non-payment.

4.2 Engineering Services & Maintenance / Support (Time & Materials)
For ongoing services including:
- Engineering support
- Production support
- Maintenance and troubleshooting
- Onsite or remote technical services
Billing structure:
- Invoiced monthly based on:
o Actual labor hours worked
o Agreed hourly labor rates
o Materials and expenses incurred
- Supporting documentation (timesheets, logs, etc.) will be provided upon request

4.3 System Integration Projects
For full-scope integration projects including:
- AMR deployment
- System design and engineering
- Installation and commissioning
- Project management and HyperCare support
Payment structure:
- 50% due upon project kickoff / PO issuance
- 40% due upon commissioning
- 10% due upon final completion and customer acceptance
Notes:
- This structure applies to all services, labor, travel, and project-related expenses, unless otherwise specified
- Hardware and third-party equipment are excluded and billed separately under Section 4.5

4.4 Deliverable-Based Work
For defined-scope projects with a specific output or result (e.g., studies, designs, reports, simulations):
Payment structure:
- 80% due upon project initiation
- 20% due upon completion and customer acceptance

4.5 Hardware & Equipment Sales
For all hardware, including but not limited to:
- Robotics Equipment
- Intralogistics Software
- Mechanical, Electrical, Pneumatic Equipment
- System Peripherals
Procurement and Supplier Dependency
Hardware procurement will not begin until initial payment is received
o OEM/vendor availability
o Supplier lead times
o Vendor pricing and commercial terms
- If vendor terms require:
o Higher upfront payments
o Non-cancellable commitments
o Accelerated payment schedules
These requirements may be passed through to the Customer.
Commercial Terms and Taxes
- All prices are exclusive of taxes.
- Customer is responsible for all applicable:
o Sales tax
o Use tax
o VAT
o Duties, tariffs, or import/export fees
- Freight, packaging, transportation, insurance, and installation-related costs are not included unless explicitly stated and will be invoiced separately.
Retention of Title
MAG retains ownership of all hardware and equipment until:
- Full payment has been received, and
- All outstanding balances under the applicable order are satisfied
Customer shall:
- Not pledge, resell, or encumber unpaid goods
- Notify MAG immediately if third parties attempt to seize or claim such goods
- Take reasonable care of all delivered but unpaid equipment

4.6 Payment Structure Precedence
- The applicable payment structure will be defined by the type of engagement as outlined above
- In the event of mixed scope (e.g., integration + hardware + support), each portion will be billed according to its respective structure
- Any deviations must be agreed to in writing by both MAG and Customer

4.7 Pricing Adjustments and Vendor-Driven Terms
MAG reserves the right to adjust pricing prior to shipment or execution due to:
- Supplier cost increases
- Tariffs, duties, or regulatory changes
- Material or component price fluctuations
In addition, Customer acknowledges that third-party vendors and OEM suppliers may impose specific commercial terms, including but not limited to:
- Upfront payment requirements
- Accelerated payment schedules
- Non-cancellable or non-refundable purchase commitments
To the extent such vendor requirements apply to the project, MAG reserves the right to modify payment terms, invoicing structure, and project milestones accordingly, including requiring additional upfront payments from Customer.
MAG will make reasonable efforts to communicate such changes in advance; however, compliance with vendor-imposed terms is a condition of procurement and project execution.
Any delays in Customer payments required to satisfy vendor terms may result in:
- Procurement delays
- Schedule impacts
- Additional costs

5. Delivery and Acceptance
5.1 Delivery Terms and Schedule
Delivery terms shall be FCA (Incoterms 2020) from MAG's facility or FCA Customer's designated location, as specified in the applicable quotation.
Title and risk of loss transfer to the Customer upon delivery to the first carrier.
All delivery and shipping dates provided by MAG are estimates only and are contingent upon:
- Timely receipt of Customer approvals
- Receipt of required payments
- Availability of materials and supplier/OEM lead times
MAG shall not be liable for delays resulting from:
- Supplier or OEM performance issues
- Material shortages or supply chain disruptions
- Transportation or carrier delays
- Customer acts or omissions
- Site readiness or access limitations
- Force majeure events or other circumstances beyond MAG's reasonable control
Such delays shall not constitute a breach of contract and may result in schedule adjustments.

5.2 Freight, Shipping, and Risk of Loss
Unless otherwise specified in writing:
- Shipments are made FCA / FOB Origin
- Freight charges are Prepaid and Added to the invoice unless Customer designates a preferred carrier
- Title and risk of loss transfer to Customer upon delivery to the carrier
Customer is responsible for:
- Unloading and rigging
- Receiving and inspection of goods upon delivery
Additional charges for shipping, handling, customs, duties, or insurance (if applicable) will be invoiced separately.

5.3 Freight Claims and Damages
- For Customer-arranged freight, Customer is solely responsible for filing claims directly with the carrier
- For MAG-arranged freight, Customer must report any damage, shortage, or discrepancy within ten (10) days of delivery
Failure to properly document damage at time of receipt (including noting damage with the carrier and retaining packaging) may impact claim eligibility.
Replacement products must be ordered under a new purchase order.

5.4 Inspection and Acceptance
Customer shall inspect all goods and services within five (5) days of delivery ("Inspection Period").
Any claims for:
- Defects
- Shortages
- Non-conformance
- Damage
must be submitted to MAG in writing within the Inspection Period.
Failure to provide written notice within this period shall:
- Constitute irrevocable acceptance of the goods and services
- Act as a waiver of all related claims
This inspection and acceptance requirement applies to each partial shipment or delivery under any order.
Factory Acceptance Testing (FAT)
Where applicable, MAG will perform Factory Acceptance Testing (FAT) based on the agreed system design and functional criteria.
- FAT criteria and methods will be defined in the project documentation or scope of work
- Upon completion, results will be documented and shared
If Customer:
- Does not attend FAT, or
- Fails to provide feedback within a reasonable timeframe
then FAT shall be deemed accepted as completed

5.5 Equipment and Third-Party Supply
MAG supplies equipment including, but not limited to:
- Robotics Equipment
- Intralogistics Software
- Mechanical, Electrical, Pneumatic Equipment
- System Peripherals
All equipment is subject to:
- OEM specifications, warranty, and limitations
- Supplier availability and lead times
- Vendor pricing, policies, and commercial terms
MAG is not responsible for delays, defects, or performance limitations attributable to third-party suppliers beyond MAG's defined integration scope.

6. Warranty
6.1 Services Warranty
MAG warrants that all services performed - including but not limited to:
- Engineering and design services
- System integration and deployment
- Installation supervision and commissioning
- Production support, maintenance, and troubleshooting
- Training and operational support
- Deliverable-based work (e.g., studies, layouts, simulations, documentation)
will be performed in a professional and workmanlike manner by qualified personnel, consistent with generally accepted industry standards.
This warranty applies to all service types, including:
- Time and materials services
- Fixed-scope system integration projects
- Deliverable-based engagements
Customer's exclusive remedies for breach of this warranty shall be, at MAG's sole discretion:
1. Re-performance of the non-conforming services; or
2. Issuance of a credit not to exceed the fees paid for the specific services giving rise to the claim

6.2 System Performance and Operational Disclaimer
MAG does not warrant that:
- System operation will be uninterrupted or error-free
- AMR or automation systems will achieve specific throughput, uptime, or performance metrics unless expressly defined in writing
- Systems will operate without dependency on proper Customer use, maintenance, staffing, and environmental conditions
System performance is dependent on factors outside of MAG's control, including but not limited to:
- Facility layout and constraints
- Operator interaction
- Upstream and downstream processes
- Customer-provided equipment and materials
- Integration with third-party systems

6.3 Third-Party Products and Equipment
All third-party equipment and software, including but not limited to:
- Robotics Equipment
- Intralogistics Software
- Mechanical, Electrical, Pneumatic Equipment
- System Peripherals
are subject solely to the warranties provided by their respective manufacturers or vendors.
Manufacturing Automation Group LLC (MAG):
- Passes through such warranties to the extent permitted
- Provides no independent warranty on third-party equipment
- Is not responsible for vendor defects, limitations, or performance issues beyond integration scope

6.4 Specifications and Scope Responsibility
MAG will integrate, install, and commission systems in accordance with:
- The defined scope of work
- Agreed system design and specifications
To the extent that specifications, requirements, or standards are provided by the Customer or third parties:
- MAG's responsibility is limited to implementation within the agreed scope
- Final compliance, validation, and approval of such requirements remain the responsibility of the Customer and its designated stakeholders

6.5 Warranty Disclaimer
THE WARRANTIES SET FORTH IN THIS SECTION ARE EXCLUSIVE AND IN LIEU OF ALL OTHER WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
- IMPLIED WARRANTIES OF MERCHANTABILITY
- FITNESS FOR A PARTICULAR PURPOSE
- NON-INFRINGEMENT
MAG MAKES NO GUARANTEE OF SYSTEM PERFORMANCE BEYOND WHAT IS EXPRESSLY STATED IN WRITING.

7. Limitation of Liability
TO THE FULLEST EXTENT PERMITTED BY LAW, MAG SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, OR CONSEQUENTIAL DAMAGES, INCLUDING WITHOUT LIMITATION LOST PRODUCTION, LOST PROFITS, LOST REVENUE, LOSS OF DATA, OR BUSINESS INTERRUPTION, EVEN IF MAG HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
MAG's maximum cumulative liability for any claim, whether in contract, tort, or otherwise, shall not exceed the total fees paid by Customer to MAG for the specific statement of work or deliverable giving rise to the claim. Each limitation and exclusion in these Terms is independent and shall be enforced to the maximum extent permitted by applicable law.
CLAIMS DEADLINE: Any claim or cause of action arising out of or related to these Terms, or any goods or services provided by MAG, must be brought within one (1) year of the date on which the claim arose. Any claim not brought within this period is permanently barred, regardless of when it was discovered. This limitation applies to all claims of any kind, whether based in contract, tort, warranty, or otherwise.

8. Intellectual Property
All intellectual property developed, created, or utilized by MAG in connection with any project, including methodologies, tools, software, designs, and documentation, remains the sole property of MAG. MAG grants Customer a non-exclusive, non-transferable license to use MAG deliverables solely for Customer's internal operations associated with the applicable project. MAG provides no indemnification for Customer-specified systems, designs, or third-party products. Intellectual property rights in third-party equipment or software are governed exclusively by the applicable vendor licenses and terms.

9. Order Cancellation
9.1 Notice of Cancellation
All cancellations must be submitted in writing and are only effective upon written acknowledgment by MAG. MAG reserves the right, at its sole discretion, to approve or reject any cancellation request.

9.2 Engineering Services and Time & Materials Work
For engineering services, production support, maintenance, or other time-and-materials work:
Customer shall be invoiced for:
- All labor hours performed through the effective date of cancellation
- Any materials, travel, or expenses incurred

9.3 System Integration Projects
For system integration projects (including AMR deployments and full-scope implementations):
- All milestone payments invoiced prior to cancellation remain non-refundable
- Customer shall be responsible for:
o All work performed to date
o All committed costs (including labor, engineering, materials, and subcontractors)
- If cancellation occurs after project kickoff, Customer shall pay:
o All costs incurred to date plus a cancellation fee of twenty percent (20%) of the remaining project value

9.4 Deliverable-Based Work
For fixed-scope, deliverable-based engagements:
- Initial payments (including the 80% upfront portion) are non-refundable once work has commenced
- Customer shall be responsible for:
o All work completed
o Any committed or incurred costs
- If cancellation occurs prior to completion, MAG may invoice for work performed and retain applicable prepaid amounts

9.5 Hardware and Materials
For hardware, equipment, and third-party products:
- Orders canceled after procurement are subject to:
o All costs incurred by MAG, including materials, labor, and supplier commitments
o Applicable OEM/vendor cancellation charges
o A minimum cancellation fee of:
▪ 20% for standard items, or
▪ 30% for custom or non-stock items, whichever is greater
- If vendor terms impose stricter cancellation or non-cancellable conditions, those terms shall flow through to the Customer

9.6 Late-Stage Cancellation
If cancellation occurs after:
- Shipment authorization
- Equipment delivery
- Or commencement of installation or commissioning
Customer shall be liable for:
- 100% of costs incurred, plus
- A thirty percent (30%) cancellation fee on any remaining unbilled portion of the order

9.7 Minimum Liability and Non-Cancellable Commitments
Cancellation does not relieve Customer of responsibility for:
- All costs incurred by MAG in good-faith performance
- Any non-cancellable commitments made with suppliers or subcontractors
- Any obligations required to unwind or terminate project execution
MAG's determination of incurred and committed costs shall be final and binding, acting reasonably and in good faith.

10. Returns
Any All returns require prior written authorization from MAG. Non-warranty returns of unused, uninstalled, and resalable items are subject to MAG's return policies in effect at the time of return, including applicable restocking charges. Non-stock and custom items are non-returnable. All authorized returned items must be properly packed and shipped freight prepaid by Customer to the MAG-designated location.

11. Order Changes
Any modifications to a confirmed order, including changes to scope, specifications, requirements, or delivery, must be documented in writing and are subject to MAG's prior written approval. Approved changes may result in adjustments to price, delivery schedule, or other terms. MAG reserves the right to decline changes that are unsafe, technically infeasible, or inconsistent with MAG's design, engineering, or quality standards.

12. Force Majeure
MAG shall not be liable for any loss, damage, or delay caused by events beyond its reasonable control, including but not limited to acts of God, fire, flood, strikes, labor disturbances, material shortages, transportation delays, natural disasters, acts of civil or military authority, epidemics, pandemics, or governmental restrictions. In such cases, affected performance deadlines shall be extended for a period equal to the duration of the delay. If a force majeure event continues for thirty (30) or more consecutive days, either party may terminate the affected order upon written notice.

13. Export and Trade Compliance
Products and services provided by MAG may be subject to U.S. export control laws, regulations, and sanctions programs. Customer agrees to comply fully with all applicable export and re-export laws, embargoes, and restrictions. Customer represents and warrants that no sanctioned or restricted parties will be involved in any transaction with MAG and that Customer will obtain all necessary governmental authorizations for any subsequent export, import, or use of products purchased from MAG.

14. Confidentiality
Each party may receive confidential or proprietary information of the other in connection with a project ("Confidential Information"). Each party agrees to hold the other's Confidential Information in strict confidence, not to disclose it to third parties without prior written consent, and to use it solely for the purpose of fulfilling obligations under the applicable engagement. Confidential Information does not include information that is or becomes publicly known through no fault of the receiving party, or that is independently developed by the receiving party without reference to the disclosing party's information.

15. Governing Law and Dispute Resolution
These Terms are governed by and construed under the laws of the State of Michigan, USA, excluding its conflict-of-law rules and the United Nations Convention on Contracts for the International Sale of Goods (CISG).
Disputes shall be resolved in the following order:
- Good-faith negotiation between the parties;
- If unresolved after thirty (30) days, non-binding mediation, with mediator fees shared equally; then
- If still unresolved, litigation in the state or federal courts located in Wayne County, Michigan, which shall have exclusive jurisdiction.
Each party irrevocably consents to personal jurisdiction and venue in those courts and waives any objection to the laying of venue therein.

16. Assignment
Customer may not assign or transfer any rights or obligations arising under these Terms without the prior written consent of MAG. MAG may assign its rights and obligations to any affiliate or in connection with a merger, acquisition, or corporate reorganization without Customer's consent. Any attempted assignment by Customer in violation of this section is null and void.

17. Changes to These Terms
MAG reserves the right to update or revise these Terms at any time. Updated Terms will be posted on our website with a revised Effective Date. For ongoing engagements, material changes will be communicated in writing. Your continued engagement with MAG following notice of updated Terms constitutes acceptance of the revised Terms.

18. Severability
If any provision of these Terms is found to be invalid or unenforceable, that provision will be modified to the minimum extent necessary to make it enforceable, and the remaining provisions will continue in full force and effect.

19. Entire Agreement
These Terms, together with any applicable quotation, statement of work, or project-specific agreement, constitute the entire agreement between MAG and Customer with respect to the subject matter hereof, and supersede all prior discussions, representations, and understandings, whether written or oral.

20. Contact Us
If you have questions about these Terms or wish to reach our legal team regarding a dispute or inquiry, please contact us at:
Manufacturing Automation Group LLC (MAG)
35572 Veronica St., Livonia, MI 48150
Email: solutions@mag-automate.com
Website: www.mag-automate.com

© 2026 Manufacturing Automation Group LLC. All Rights Reserved. Reproduction for commercial purposes is prohibited without written consent from MAG.`;

  const lines = termsText.split("\n");

  return (
    <div className="max-w-6xl mx-auto px-4 py-12">
      <Seo
        noindex
        title="Terms of Service | Manufacturing Automation Group (MAG)"
        description="Read the official Manufacturing Automation Group LLC Terms of Service."
        path="/terms-of-service"
      />
      <section className="border border-gray-200 bg-white p-6 shadow-sm sm:p-8 lg:p-10">
        <div className="text-base leading-relaxed text-gray-600">
          {lines.map((line, idx) => {
            if (line.trim() === "") return <div key={idx} className="h-4" />;

            const isTopHeader =
              line === "MANUFACTURING AUTOMATION GROUP LLC" ||
              line === "Terms of Service";
            const isSectionTitle = /^\d+\.\s/.test(line);
            const isSubsectionTitle = /^\d+\.\d+\s/.test(line);
            const isImportant = line.startsWith("IMPORTANT:");
            const isAllCapsLine =
              line === line.toUpperCase() && /[A-Z]/.test(line) && line.length > 16;
            const isLabelLine = /:$/.test(line) && !line.startsWith("-");

            const classes = isTopHeader
              ? line === "Terms of Service"
                ? "whitespace-pre-wrap break-words font-bold tracking-tight text-gray-900 text-3xl sm:text-4xl"
                : "whitespace-pre-wrap break-words font-bold tracking-tight text-gray-900 text-lg sm:text-xl"
              : isSectionTitle
              ? "whitespace-pre-wrap break-words mt-2 font-bold tracking-tight text-gray-900 text-lg sm:text-xl"
              : isSubsectionTitle
              ? "whitespace-pre-wrap break-words mt-1 font-semibold tracking-tight text-gray-900"
              : isImportant || isAllCapsLine || isLabelLine
              ? "whitespace-pre-wrap break-words font-semibold text-gray-900"
              : "whitespace-pre-wrap break-words";

            const Tag = line === "Terms of Service" ? "h1" : "p";

            return (
              <Tag key={idx} className={classes}>
                {line}
              </Tag>
            );
          })}
        </div>
      </section>
    </div>
  );
}
